import { EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { createSalesforceLead, upsertSalesforceInsuranceLead } from 'domains/signupv4/Leads/queries';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { mergeCommunicationConcerns } from 'domains/signupDirect/misc/communicationConcerns';
import { getInsuranceSubscriberDetails } from 'domains/signupDirect/Evaluation/Insurance/eligibility';
import { getSFInsuranceType } from 'domains/signupv4/Parent/actors';
import { getCandidRelationshipID } from 'domains/client';
import { InsuranceStatus, VerificationTypes } from 'domains/signup/data/types';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { LeadDesiredPaymentMethod, CreateSignupV4InsuranceLeadPayload } from 'domains/signupv4/Leads/types';
import { DirectSignupFilters } from 'domains/signupDirect/context';
import { getTempLeadId, setTempLeadId } from 'domains/signupDirect/misc/leadStorage';
import { EligibilityCheckResult } from 'domains/signupDirect/SyntaxComps/EligibilityCard';
import { toStateFull, StateAbbrev } from 'utils/states';

/**
 * Creates a Salesforce lead if one doesn't already exist
 */
export const createLeadOnEligibilityCheck = async (
  data: EvaluationFormType,
  getValues: UseFormGetValues<EvaluationFormType>,
  setValue: UseFormSetValue<EvaluationFormType>,
  filters: DirectSignupFilters,
  eligibilityCheckResult?: EligibilityCheckResult,
) => {
  // Try to get leadId from form data first, then from localStorage as fallback.
  const existingLeadId = getValues('leadId') ?? getTempLeadId();
  const existingInsuranceLeadId = getValues('insuranceLeadId');

  let leadId = existingLeadId;

  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  // Only create a new lead if one doesn't exist
  if (!leadId) {
    const leadObject = {
      address: {
        state: filters.stateCode,
      },
      email: data.contactInfo.email,
      firstName: data.clientInfo.firstName,
      lastName: data.clientInfo.lastName,
      phone: data.contactInfo.phone,
      selfSignUp: true,
      dob: data.clientInfo.dateOfBirth,
      communicationConcerns: mergeCommunicationConcerns(data.concerns.clinicalConcernKeys, data.concerns.clinicalAreas),
      detectedAreas: data.concerns.clinicalAreas,
      desiredPaymentMethod: 'Insurance' as LeadDesiredPaymentMethod,
      leadSource: 'Self-Referral - Web Inquiry',
      serviceFor: data.clientType,
      relationshipToClient: data.contactInfo.relationshipToClient,
      signupFlowOutcome: 'Abandoned Form Without Booking',
      sourceSystem: 'Signup Direct',
      contactTimezone: data.evaluationSchedule.timeZone,
      ...UTMParams,
      ...GCLIDParams,
    };
    const leadResponse = await createSalesforceLead(leadObject);
    leadId = leadResponse.id;

    setTempLeadId(leadId);
    setValue('leadId', leadId);
  }

  if (data.billingInfo.type !== 'privatePay') {
    const subscriberDetails = getInsuranceSubscriberDetails(data);
    const insuranceCheckResult = eligibilityCheckResult ?? data.billingInfo.eligibilityCheckResult;

    const getInsuranceStatus = () => {
      if (insuranceCheckResult && insuranceCheckResult === EligibilityCheckResult.IN_NETWORK) {
        return InsuranceStatus.ACTIVE;
      }

      // Return NOT_CHECKED for error values
      if (
        insuranceCheckResult === EligibilityCheckResult.ERROR ||
        insuranceCheckResult === EligibilityCheckResult.INCORRECT_DOB ||
        insuranceCheckResult === EligibilityCheckResult.INCORRECT_MEMBER_ID ||
        insuranceCheckResult === EligibilityCheckResult.INCORRECT_NAME ||
        insuranceCheckResult === EligibilityCheckResult.INVALID_PARTICIPANT_ID
      ) {
        return InsuranceStatus.NOT_CHECKED;
      }

      return InsuranceStatus.INACTIVE;
    }

    const insuranceStatus = getInsuranceStatus();
    const insuranceLeadParams: CreateSignupV4InsuranceLeadPayload = {
      insuranceLeadID: existingInsuranceLeadId ?? null,
      leadID: leadId,
      insuranceType: getSFInsuranceType(data.billingInfo.insuranceData.insuranceType, false),
      payerPlanName: data.billingInfo.insuranceData.insuranceName,
      payerNameClaims: data.billingInfo.insuranceData.candidPayerName,
      payerState: data.billingInfo.insuranceData.state
        ? toStateFull(data.billingInfo.insuranceData.state as StateAbbrev)
        : undefined,
      payerForClaims: data.billingInfo.insuranceData.payerId,
      memberID: data.billingInfo.memberId ?? '',
      subscriber: {
        firstName: subscriberDetails.firstName,
        lastName: subscriberDetails.lastName,
        dateOfBirth: new Date(subscriberDetails.dateOfBirth),
        relationshipToClient: getCandidRelationshipID(data.contactInfo.relationshipToClient),
      },
      insuranceStatus: insuranceStatus,
      coPayAmount:
        data.billingInfo.insuranceData.averageSessionPatientResponsibility ??
        data.billingInfo.insuranceData.defaultDeposit,
    };

    // Only add verificationType if insurance status is not NOT_CHECKED
    if (insuranceStatus !== InsuranceStatus.NOT_CHECKED) {
      insuranceLeadParams.verificationType = VerificationTypes.AUTOMATED;
    }

    // Create insurance object
    const insuranceLeadResponse = await upsertSalesforceInsuranceLead(insuranceLeadParams);

    setValue('insuranceLeadId', insuranceLeadResponse.id);
  }
};
